import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bell, LogOut, LayoutDashboard, Wallet, Store, ChevronDown, UtensilsCrossed, Shield, FileText, Globe, UserX, Loader2, UserCog, Heart, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SupportDialog from '@/components/SupportDialog';
import { isBusiness } from '@/lib/roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const { user, login, logout, setUser } = useAuth();
  const { t, lang, switchLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  // Account deletion dialog (Phase 21 UI)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const isEmailUser = !!user && !!user.password_hash;  // Heuristic; backend will enforce
  // NOTE: /auth/me does NOT expose password_hash — we can only infer by auth_provider if available
  const hasEmailAuth = !!user && (user.auth_provider === 'email' || !!user.password_hash);
  const [notifications, setNotifications] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/notifications`, { withCredentials: true });
        setNotifications(res.data);
        setUnreadCount(res.data.filter(n => !n.read).length);
      } catch (err) {
        console.error('Failed to fetch notifications', err);
      }
    })();
  }, [user, location.pathname]);

  const markRead = async () => {
    try {
      await axios.put(`${API}/notifications/read`, {}, { withCredentials: true });
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark notifications as read', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;  // anti double-submit
    setDeleting(true);
    setDeleteError('');
    try {
      const payload = { confirm: 'DELETE' };
      if (hasEmailAuth) payload.password = deletePassword;
      await axios.delete(`${API}/users/me`, { data: payload, withCredentials: true });
      // Success: close dialog, clear local session, logout + redirect
      setShowDeleteDialog(false);
      try { sessionStorage.removeItem('womio_user_v1'); } catch (e) {}
      setUser(null);
      toast.success(t('da_success_toast'));
      navigate('/', { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const code = (typeof d === 'object' && d?.code) || null;
      const message = (typeof d === 'object' && d?.message) || (typeof d === 'string' ? d : '');
      if (code === 'INVALID_PASSWORD') {
        setDeleteError(t('da_err_invalid_password'));
      } else if (code === 'PASSWORD_REQUIRED') {
        setDeleteError(t('da_err_password_required'));
      } else if (code === 'CONFIRMATION_REQUIRED') {
        setDeleteError(t('da_err_confirmation'));
      } else if (code === 'ROLE_NOT_SELF_DELETABLE') {
        setDeleteError(message || t('da_err_role'));
      } else {
        setDeleteError(t('da_err_generic'));
      }
    } finally {
      setDeleting(false);
    }
  };
  const canConfirmDelete = confirmText.trim().toUpperCase() === 'DELETE'
    && (!hasEmailAuth || deletePassword.length > 0)
    && !deleting;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <nav className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-8">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img src="/logo-wordmark@2x.png" alt="Womio" className="h-7 sm:h-8 w-auto" />
            </Link>
            {user && (
              <div className="hidden md:flex items-center gap-1">
                {!isBusiness(user) && (
                  <>
                    <Link to="/dashboard" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/dashboard') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-dashboard">
                      {t('nav_dashboard')}
                    </Link>
                    <Link to="/restaurants" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/restaurants') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-restaurants">
                      {t('nav_restaurants')}
                    </Link>
                    <Link to="/my-reviews" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/my-reviews') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-my-reviews">
                      {t('nav_my_reviews')}
                    </Link>
                    <Link to="/wallet" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/wallet') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-wallet">
                      {t('nav_wallet')}
                    </Link>
                    <Link to="/social-projects" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/social-projects') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-social-projects">
                      {t('nav_social_projects')}
                    </Link>
                  </>
                )}
                {isBusiness(user) && (
                  <>
                    <Link to="/restaurant/dashboard" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${location.pathname === '/restaurant/dashboard' ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-restaurant-dashboard">
                      {t('nav_restaurant_dashboard')}
                    </Link>
                    <Link to="/restaurant/profile" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${location.pathname === '/restaurant/profile' ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-restaurant-profile">
                      {t('nav_my_restaurant')}
                    </Link>
                  </>
                )}
                {user.admin_permissions?.includes('admin.read') && (
                  <>
                    <Link to="/admin" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/admin') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-admin">
                      {t('nav_admin')}
                    </Link>
                    <Link to="/admin/social-projects" className={`px-3 py-1.5 rounded-full text-sm font-sans transition-colors ${isActive('/admin/social-projects') ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-admin-social-projects">
                      {t('nav_admin_social_projects')}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Language Switch */}
            <button onClick={() => switchLang(lang === 'it' ? 'en' : 'it')} className="p-2 rounded-full hover:bg-secondary transition-colors text-muted-foreground" data-testid="lang-switch">
              <Globe className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {user ? (
              <>
                {/* Notifications */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative p-2 rounded-full hover:bg-secondary transition-colors" data-testid="notifications-btn">
                      <Bell className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      {unreadCount > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-destructive text-destructive-foreground rounded-full text-[10px] flex items-center justify-center font-semibold">{unreadCount}</span>}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 rounded-xl">
                    <div className="px-3 py-2 flex items-center justify-between">
                      <p className="text-sm font-semibold font-sans">{t('nav_notifications')}</p>
                      {unreadCount > 0 && <button onClick={markRead} className="text-xs text-primary font-sans">{t('nav_mark_read')}</button>}
                    </div>
                    <DropdownMenuSeparator />
                    {notifications.length === 0 ? (
                      <div className="px-3 py-4 text-center text-sm text-muted-foreground font-sans">{t('nav_no_notifications')}</div>
                    ) : notifications.slice(0, 5).map((n, i) => (
                      <DropdownMenuItem key={n.notification_id || i} className="px-3 py-2 cursor-default">
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                          <div>
                            <p className="text-xs font-sans leading-relaxed">{n.message}</p>
                            <p className="text-[10px] text-muted-foreground font-sans mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-secondary transition-colors" data-testid="user-menu-btn">
                      {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">{user.name?.[0]}</div>}
                      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid="user-dropdown">
                    <div className="px-3 py-2">
                      <p className="text-sm font-semibold font-sans">{user.name}</p>
                      <p className="text-xs text-muted-foreground font-sans">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {!isBusiness(user) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer"><LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_dashboard')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/my-reviews')} className="cursor-pointer"><FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_my_reviews')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/wallet')} className="cursor-pointer"><Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_wallet')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/restaurants')} className="cursor-pointer"><UtensilsCrossed className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_restaurants')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/social-projects')} className="cursor-pointer" data-testid="drop-social-projects"><Heart className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_social_projects')}</DropdownMenuItem>
                      </>
                    )}
                    {isBusiness(user) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/restaurant/dashboard')} className="cursor-pointer" data-testid="drop-restaurant-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_restaurant_dashboard')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/restaurant/profile')} className="cursor-pointer" data-testid="drop-restaurant-profile"><Store className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_my_restaurant')}</DropdownMenuItem>
                      </>
                    )}
                    {user.admin_permissions?.includes('admin.read') && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer"><Shield className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_admin')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/social-projects')} className="cursor-pointer" data-testid="drop-admin-social-projects"><Heart className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_admin_social_projects')}</DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setSupportOpen(true)} className="cursor-pointer" data-testid="drop-support"><LifeBuoy className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_support')}</DropdownMenuItem>
                    <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} className="cursor-pointer text-destructive" data-testid="drop-logout"><LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_sign_out')}</DropdownMenuItem>
                    {user.role === 'user' && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/profile/edit')} className="cursor-pointer" data-testid="drop-edit-profile"><UserCog className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_edit_profile')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setConfirmText(''); setDeletePassword(''); setDeleteError(''); setShowDeleteDialog(true); }} className="cursor-pointer text-muted-foreground hover:text-destructive focus:text-destructive" data-testid="drop-delete-account"><UserX className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('nav_delete_account')}</DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button data-testid="navbar-login-btn" onClick={() => login('user')} size="sm" className="rounded-full btn-lift">{t('nav_sign_in')}</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      {user && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border md:hidden" data-testid="mobile-nav">
          <div className="flex items-center justify-around py-2 px-2">
            {(isBusiness(user) ? [
              { to: '/restaurant/dashboard', icon: <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_restaurant_dashboard') },
              { to: '/restaurant/profile', icon: <Store className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_my_restaurant') },
            ] : [
              { to: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_dashboard') },
              { to: '/restaurants', icon: <UtensilsCrossed className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_restaurants') },
              { to: '/my-reviews', icon: <FileText className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_my_reviews') },
              { to: '/wallet', icon: <Wallet className="w-5 h-5" strokeWidth={1.5} />, label: t('nav_wallet') },
            ]).map(item => (
              <Link key={item.to} to={item.to} className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors ${isActive(item.to) ? 'text-primary' : 'text-muted-foreground'}`} data-testid={`mobile-nav-${item.to.slice(1).replace('/','-')}`}>
                {item.icon}
                <span className="text-[10px] font-sans font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Phase 21 — Delete account dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!deleting) setShowDeleteDialog(open); }}>
        <DialogContent className="rounded-2xl max-w-md mx-4" data-testid="delete-account-dialog" onPointerDownOutside={(e) => { if (deleting) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl sm:text-2xl flex items-center gap-2 text-destructive">
              <UserX className="w-5 h-5" strokeWidth={1.5} />
              {t('da_title')}
            </DialogTitle>
            <DialogDescription className="font-sans whitespace-pre-line pt-2 text-foreground/80">
              {t('da_body')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="da-confirm" className="text-sm font-sans">{t('da_confirm_label')}</Label>
              <Input id="da-confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" autoComplete="off" disabled={deleting} data-testid="da-confirm-input" className="font-mono mt-1" />
            </div>
            {hasEmailAuth && (
              <div>
                <Label htmlFor="da-password" className="text-sm font-sans">{t('da_password_label')}</Label>
                <Input id="da-password" type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} autoComplete="current-password" disabled={deleting} data-testid="da-password-input" className="mt-1" />
              </div>
            )}
            {deleteError && (
              <p className="text-sm text-destructive font-sans" data-testid="da-error">{deleteError}</p>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting} className="rounded-full" data-testid="da-cancel-btn">
              {t('da_cancel')}
            </Button>
            <Button onClick={handleDeleteAccount} disabled={!canConfirmDelete} variant="destructive" className="rounded-full btn-lift" data-testid="da-confirm-btn">
              {deleting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('da_deleting')}</>) : (<><UserX className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('da_confirm_btn')}</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
    </>
  );
}
